// src/pages/Articles.js
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";

import BASE_URL, { DAJC } from "../config";
import {
  getTitle, getImage, getExcerpt, formatDate, localePath,
} from "../utils/content";
import Pagination from "../components/Pagination";
import Seo from "../components/Seo";

/** Inline SVG fallback rendered when an article has no image */
function ArticleImageFallback({ title = "" }) {
  const initials = title
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

  return (
    <div className="card-article__media-fallback" aria-hidden="true">
      <svg
        viewBox="0 0 320 200"
        xmlns="http://www.w3.org/2000/svg"
        width="100%"
        height="100%"
        style={{ display: "block" }}
      >
        {/* Background */}
        <defs>
          <linearGradient id="fallbackGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="var(--ink, #0d1b2a)" stopOpacity="1" />
            <stop offset="100%" stopColor="#1a2e45" stopOpacity="1" />
          </linearGradient>
        </defs>
        <rect width="320" height="200" fill="url(#fallbackGrad)" />

        {/* Decorative scales of justice icon */}
        <g transform="translate(100, 30)" stroke="var(--gold, #c9a84c)" strokeWidth="1.5" fill="none" opacity="0.18">
          <line x1="60" y1="0" x2="60" y2="120" />
          <line x1="20" y1="140" x2="100" y2="140" />
          <line x1="35" y1="30" x2="85" y2="30" />
          <path d="M35 30 l-20 40 a20 20 0 0 0 40 0 l-20-40z" />
          <path d="M85 30 l-20 40 a20 20 0 0 0 40 0 l-20-40z" />
        </g>

        {/* Corner accent lines */}
        <line x1="0" y1="0" x2="40" y2="0" stroke="var(--gold, #c9a84c)" strokeWidth="2" opacity="0.4" />
        <line x1="0" y1="0" x2="0" y2="40" stroke="var(--gold, #c9a84c)" strokeWidth="2" opacity="0.4" />
        <line x1="320" y1="200" x2="280" y2="200" stroke="var(--gold, #c9a84c)" strokeWidth="2" opacity="0.4" />
        <line x1="320" y1="200" x2="320" y2="160" stroke="var(--gold, #c9a84c)" strokeWidth="2" opacity="0.4" />

        {/* Initials or icon */}
        {initials ? (
          <text
            x="160"
            y="115"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="var(--gold, #c9a84c)"
            fontSize="42"
            fontFamily="Georgia, serif"
            letterSpacing="4"
            opacity="0.9"
          >
            {initials}
          </text>
        ) : (
          <text
            x="160"
            y="115"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="var(--gold, #c9a84c)"
            fontSize="48"
            opacity="0.7"
          >
            ⚖
          </text>
        )}
      </svg>
    </div>
  );
}

export default function Articles() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [articles, setArticles] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [perPage, setPerPage] = useState(9);
  const [debounced, setDebounced] = useState("");

  const cancelRef = useRef(null);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(searchTerm), 400);
    return () => clearTimeout(id);
  }, [searchTerm]);

  useEffect(() => { setPage(1); }, [debounced, perPage]);

  useEffect(() => {
    if (cancelRef.current) cancelRef.current.cancel();
    cancelRef.current = axios.CancelToken.source();
    setLoading(true);

    axios
      .get(`${BASE_URL}/api/categoryportail/edit/${DAJC.CATEGORY_ID}`, {
        params: { page, per_page: perPage, search: debounced },
        cancelToken: cancelRef.current.token,
      })
      .then((r) => {
        setArticles(r.data?.articles?.data || []);
        setTotalPages(r.data?.articles?.pagination?.last_page || 1);
        setLoading(false);
      })
      .catch((err) => {
        if (!axios.isCancel(err)) setLoading(false);
      });
  }, [page, perPage, debounced]);

  return (
    <>
      <Seo
        path="publications"
        title={t("articles.title")}
        description={t("articles.subtitle")}
        breadcrumb={[
          { name: t("common.home"), path: "" },
          { name: t("menu.publications"), path: "publications" },
        ]}
      />

      <section className="page-header">
        <div className="container">
          <div className="page-header__inner">
            <div className="page-header__crumbs">
              <Link to={localePath(lang)}>{t("common.home")}</Link>
              <span>/</span>
              <span>{t("menu.publications")}</span>
            </div>
            <h1>{t("articles.title")}</h1>
            <p>{t("articles.subtitle")}</p>
          </div>
        </div>
      </section>

      <section style={{ background: "var(--ivory)" }}>
        <div className="container">
          <div className="filter-bar">
            <div className="filter-bar__search">
              <i className="bi bi-search" />
              <input
                type="text"
                placeholder={t("search.placeholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm("")} aria-label="Clear">
                  <i className="bi bi-x-lg" />
                </button>
              )}
            </div>
            <select value={perPage} onChange={(e) => setPerPage(Number(e.target.value))}>
              <option value={6}>6 / {t("search.perPage")}</option>
              <option value={9}>9 / {t("search.perPage")}</option>
              <option value={12}>12 / {t("search.perPage")}</option>
              <option value={24}>24 / {t("search.perPage")}</option>
            </select>
          </div>

          {loading ? (
            <div className="loading"><div className="spinner" /><p>{t("common.loading")}</p></div>
          ) : articles.length === 0 ? (
            <div className="empty">
              <h3>{t("search.noResults")}</h3>
              <p>{t("search.tryDifferentTerms")}</p>
            </div>
          ) : (
            <div className="cards-grid">
              {articles.map((article) => {
                const img = getImage(article, lang);
                const title = getTitle(article, lang, 80);
                return (
                  <article key={article.id} className="card-article">
                    <div className="card-article__media">
                      {img ? (
                        <img
                          src={img}
                          alt={title}
                          loading="lazy"
                          onError={(e) => {
                            // If image URL exists but fails to load, swap to fallback
                            e.currentTarget.style.display = "none";
                            e.currentTarget.nextSibling?.style &&
                              (e.currentTarget.nextSibling.style.display = "flex");
                          }}
                        />
                      ) : null}
                      {/* Always render fallback; hidden when real image loads fine */}
                      <ArticleImageFallback title={title} />
                    </div>

                    <div className="card-article__body">
                      <div className="card-article__date">
                        {formatDate(article.starts_at || article.created_at, lang, t)}
                      </div>
                      <h3 className="card-article__title">
                        <Link to={localePath(lang, `article/${article.id}/${article.slug || ""}`)}>
                          {title}
                        </Link>
                      </h3>
                      <p className="card-article__excerpt">{getExcerpt(article, lang, 22)}</p>
                      <Link
                        to={localePath(lang, `article/${article.id}/${article.slug || ""}`)}
                        className="card-article__more"
                      >
                        {t("articles.read_more")}
                      </Link>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          {totalPages > 1 && (
            <Pagination current={page} total={totalPages} onChange={setPage} />
          )}
        </div>
      </section>
    </>
  );
}